import React, { useState, useRef } from "react";
import { Upload, Play, Pause, Sparkles, Loader2 } from "lucide-react";
import { aiQuick } from "../api";

export default function Audio() {
  const [src, setSrc] = useState("");
  const [name, setName] = useState("");
  const [transcript, setTranscript] = useState("");
  const [aiOut, setAiOut] = useState("");
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const audioRef = useRef(null);
  const fileRef = useRef(null);
  const recogRef = useRef(null);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setSrc(URL.createObjectURL(f));
    setName(f.name);
    setTranscript("");
  };

  // Live mic-based transcription via Web Speech API (for live audio capture only)
  const liveTranscribe = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert("Speech recognition not supported in this browser."); return; }
    if (listening) { recogRef.current?.stop(); return; }
    const r = new SR();
    r.continuous = true;
    r.interimResults = true;
    r.lang = "en-US";
    r.onstart = () => setListening(true);
    r.onend = () => setListening(false);
    r.onerror = () => setListening(false);
    r.onresult = (ev) => {
      let final = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        if (ev.results[i].isFinal) final += ev.results[i][0].transcript + " ";
      }
      if (final) setTranscript(t => (t + " " + final).trim());
    };
    recogRef.current = r;
    r.start();
  };

  const aiSummary = async () => {
    setBusy(true);
    setAiOut("");
    const ctx = transcript ? `Transcript:\n${transcript}` : `Audio file name: ${name || "(unknown)"}`;
    try {
      const r = await aiQuick(`${ctx}\n\nProvide a summary and 3 key takeaways.`, "You are an AI audio assistant.");
      setAiOut(r.text);
    } catch { setAiOut("AI unavailable."); }
    setBusy(false);
  };

  return (
    <div className="h-full flex" data-testid="app-audio">
      <div className="flex-1 flex flex-col items-center justify-center p-8 gap-6">
        <button onClick={() => fileRef.current?.click()} className="btn-ghost flex items-center gap-2" data-testid="audio-upload">
          <Upload size={14} /> Open Audio
        </button>
        <input type="file" accept="audio/*" hidden ref={fileRef} onChange={onFile} />
        {src ? (
          <>
            <div className="text-sm text-white/70 truncate max-w-full">{name}</div>
            <audio ref={audioRef} src={src} controls className="w-full max-w-md" data-testid="audio-player" />
            <button data-testid="audio-live-transcribe" onClick={liveTranscribe} className={`btn-ghost text-xs flex items-center gap-1 ${listening ? "text-[#ff7a2e] pulse-amber" : ""}`}>
              {listening ? "Stop Live Transcription" : "Start Live Transcription (mic)"}
            </button>
          </>
        ) : (
          <div className="text-white/40 text-sm">Choose an audio file to begin</div>
        )}
      </div>
      <div className="w-96 border-l border-white/10 bg-black/30 flex flex-col">
        <div className="p-3 border-b border-white/10 flex items-center gap-1.5 text-xs uppercase tracking-widest text-white/40">
          <Sparkles size={12} color="#ff7a2e" /> AI Audio Tools
        </div>
        <div className="p-3 flex-1 overflow-y-auto space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Transcript</div>
            <textarea
              data-testid="audio-transcript"
              value={transcript}
              onChange={(e) => setTranscript(e.target.value)}
              placeholder="Live transcript appears here (or paste your own)."
              rows={6}
              className="w-full bg-white/5 border border-white/10 rounded p-2 text-xs"
            />
          </div>
          <button data-testid="audio-ai-summary" disabled={busy} onClick={aiSummary} className="btn-accent text-xs w-full flex items-center justify-center gap-1">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Summarize
          </button>
          {aiOut && <div className="text-xs whitespace-pre-wrap bg-white/5 border border-white/10 p-2 rounded">{aiOut}</div>}
        </div>
      </div>
    </div>
  );
}
